import { useState, useEffect } from 'react';
import { Cloud, Sun, CloudRain, Wind, MapPin, Thermometer } from 'lucide-react';
import { Card } from "@/components/ui/card";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";

export const WeatherWidget = () => {
  const [weather, setWeather] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState("Bangalore");
  const [location, setLocation] = useState<{ lat?: number; lon?: number }>({});

  useEffect(() => {
    // Get user's location
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lon: position.coords.longitude,
          });
          // In a real app, we would use these coordinates to get the city name
          // and weather data from a weather API
          toast.success("Location detected successfully!");
        },
        (error) => {
          console.error("Error getting location:", error);
          toast.error("Could not detect location. Using default city.");
        }
      );
    }

    // For demo purposes, showing static weather data for Bangalore
    // In production, you would use a real weather API with the coordinates
    setWeather({
      temp: 26,
      condition: 'Partly Cloudy',
      humidity: 70,
      windSpeed: 15,
      forecast: [
        { day: 'Tomorrow', temp: 27, condition: 'Sunny' },
        { day: 'Wednesday', temp: 25, condition: 'Light Rain' },
        { day: 'Thursday', temp: 26, condition: 'Partly Cloudy' }
      ]
    });
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 animate-pulse">
        <div className="h-24 flex items-center justify-center">
          <Sun className="h-8 w-8 text-blue-500 animate-spin" />
        </div>
      </Card>
    );
  }

  const getWeatherIcon = (condition: string) => {
    switch (condition.toLowerCase()) {
      case 'sunny':
        return <Sun className="h-12 w-12 text-yellow-500" />;
      case 'rain':
      case 'light rain':
        return <CloudRain className="h-12 w-12 text-blue-500" />;
      case 'partly cloudy':
        return <Cloud className="h-12 w-12 text-blue-500" />;
      default:
        return <Sun className="h-12 w-12 text-yellow-500" />;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 hover:shadow-lg transition-all duration-300">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold">{city}</h3>
            </div>
            <Badge variant="secondary" className="animate-pulse">
              Live
            </Badge>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {getWeatherIcon(weather.condition)}
              <div>
                <div className="flex items-center gap-2">
                  <Thermometer className="h-5 w-5 text-red-500" />
                  <span className="text-2xl font-bold">{weather.temp}°C</span>
                </div>
                <span className="text-sm text-muted-foreground">{weather.condition}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Cloud className="h-4 w-4 text-blue-500" />
              <span>Humidity: {weather.humidity}%</span>
            </div>
            <div className="flex items-center gap-2">
              <Wind className="h-4 w-4 text-blue-500" />
              <span>Wind: {weather.windSpeed} km/h</span>
            </div>
          </div>

          <ScrollArea className="h-24">
            <div className="space-y-2">
              {weather.forecast.map((day: any, index: number) => (
                <div key={index} className="flex items-center justify-between p-2 hover:bg-blue-50 rounded-lg">
                  <span className="text-sm font-medium">{day.day}</span>
                  <div className="flex items-center gap-2">
                    {getWeatherIcon(day.condition)}
                    <span>{day.temp}°C</span>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      </Card>
    </motion.div>
  );
};